import type {
  Flow360Case,
  Flow360Folder,
  Flow360Geometry,
  Flow360SurfaceMesh,
  Flow360VolumeMesh,
  FullConfigResponse,
} from "../../flow360/types";

export type PanelRunRisk = "low" | "medium" | "high" | "unknown";

export interface PanelTargetEstimate {
  risk: PanelRunRisk;
  label: string;
  detail: string;
  targetPanels: number | null;
  denseMatrixGb: number | null;
  relativeWork: number | null;
}

export interface Flow360PanelRunEstimate {
  confidence: "metadata" | "heuristic" | "unknown";
  sourcePanels: number | null;
  sourceDenseMatrixGb: number | null;
  estimatedVolumeCells: number | null;
  storageBytes: number | null;
  wakeIterations: number;
  mac: PanelTargetEstimate;
  cuda: PanelTargetEstimate;
  facts: string[];
  warnings: string[];
}

export interface Flow360PanelRunEstimateInput {
  folder?: Flow360Folder | null;
  geometry?: Flow360Geometry | null;
  surfaceMesh?: Flow360SurfaceMesh | null;
  volumeMesh?: Flow360VolumeMesh | null;
  case?: Flow360Case | null;
  fullConfig?: FullConfigResponse | null;
  caseId?: string | null;
  wakeIterations?: number;
}

const SURFACE_PANEL_KEYS = [
  /surface.*(face|faces|cell|cells|panel|panels|triangle|triangles)/i,
  /(face|faces|cell|cells|panel|panels|triangle|triangles).*surface/i,
  /boundary.*(face|faces|cell|cells|panel|panels)/i,
  /n[_-]?(surface)?[_-]?(faces|panels|triangles|cells)/i,
  /num(ber)?[_-]?(surface)?[_-]?(faces|panels|triangles|cells)/i,
  /face[_-]?count/i,
  /panel[_-]?count/i,
  /total[_-]?(faces|panels|triangles)/i,
];

const VOLUME_CELL_KEYS = [
  /volume.*(cell|cells|element|elements)/i,
  /(cell|cells|element|elements).*volume/i,
  /n[_-]?(cells|elements)/i,
  /num(ber)?[_-]?(cells|elements)/i,
  /cell[_-]?count/i,
  /total[_-]?(cells|elements)/i,
];

function numericValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  if (typeof value === "string" && /^[0-9][0-9,._\s]*$/.test(value.trim())) {
    const parsed = Number(value.replace(/[,_\s]/g, ""));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }
  return null;
}

function pathMatches(path: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(path));
}

function largestMatchingNumber(source: unknown, patterns: RegExp[], path = "", depth = 0): number | null {
  if (source == null || depth > 8) return null;
  const direct = numericValue(source);
  if (direct != null && pathMatches(path, patterns)) return direct;
  let best: number | null = null;

  if (Array.isArray(source)) {
    if (source.length > 0 && pathMatches(path, patterns)) best = source.length;
    for (const item of source.slice(0, 96)) {
      const value = largestMatchingNumber(item, patterns, path, depth + 1);
      if (value != null && (best == null || value > best)) best = value;
    }
    return best;
  }

  if (typeof source !== "object") return null;
  for (const [key, value] of Object.entries(source as Record<string, unknown>)) {
    const nextPath = path ? `${path}.${key}` : key;
    const directValue = numericValue(value);
    if (directValue != null && pathMatches(nextPath, patterns)) {
      best = best == null ? directValue : Math.max(best, directValue);
    }
    if (value && typeof value === "object") {
      const nested = largestMatchingNumber(value, patterns, nextPath, depth + 1);
      if (nested != null && (best == null || nested > best)) best = nested;
    }
  }
  return best;
}

function storageBytes(...values: Array<number | null | undefined>): number | null {
  const finite = values.filter((value): value is number => value != null && Number.isFinite(value) && value > 0);
  return finite.length ? Math.max(...finite) : null;
}

function panelsFromStorage(surfaceBytes?: number, volumeBytes?: number, caseBytes?: number, geometryBytes?: number): number | null {
  const surfaceMb = surfaceBytes ? surfaceBytes / 1_048_576 : 0;
  const volumeMb = volumeBytes ? volumeBytes / 1_048_576 : 0;
  const caseMb = caseBytes ? caseBytes / 1_048_576 : 0;
  const geometryMb = geometryBytes ? geometryBytes / 1_048_576 : 0;
  if (surfaceMb > 0) return Math.max(250, Math.round(surfaceMb * 850));
  if (volumeMb > 0) return Math.max(500, Math.round(volumeMb * 120));
  if (caseMb > 0) return Math.max(250, Math.round(caseMb * 90));
  if (geometryMb > 0) return Math.max(250, Math.round(geometryMb * 300));
  return null;
}

function panelsFromVolumeCells(volumeCells: number | null): number | null {
  if (!volumeCells) return null;
  return Math.max(500, Math.round(volumeCells * 0.035));
}

function denseMatrixGb(panelCount: number | null, bytesPerEntry = 8): number | null {
  if (!panelCount) return null;
  return (panelCount * panelCount * bytesPerEntry) / 1_073_741_824;
}

function recommendedMacPanels(sourcePanels: number | null): number | null {
  if (!sourcePanels) return null;
  if (sourcePanels <= 3_000) return sourcePanels;
  if (sourcePanels <= 12_000) return 4_000;
  if (sourcePanels <= 60_000) return 3_000;
  return 2_000;
}

function recommendedCudaPanels(sourcePanels: number | null): number | null {
  if (!sourcePanels) return null;
  if (sourcePanels <= 12_000) return sourcePanels;
  if (sourcePanels <= 60_000) return 16_000;
  return 24_000;
}

function workScale(targetPanels: number | null, wakeIterations: number): number | null {
  if (!targetPanels) return null;
  return (targetPanels / 2_500) ** 2 * Math.max(1, wakeIterations / 15);
}

function macRisk(sourcePanels: number | null, targetPanels: number | null, wakeIterations: number): PanelTargetEstimate {
  const denseGb = denseMatrixGb(targetPanels);
  const work = workScale(targetPanels, wakeIterations);
  if (!sourcePanels || !targetPanels || denseGb == null || work == null) {
    return {
      risk: "unknown",
      label: "Needs metadata",
      detail: "Paste a case ID or select a Flow360 project so Thread can inspect mesh size before choosing Mac vs CUDA.",
      targetPanels: null,
      denseMatrixGb: null,
      relativeWork: null,
    };
  }
  if (sourcePanels > 60_000) {
    return {
      risk: "high",
      label: "Do not run full-size on Mac",
      detail: `Use a ${formatPanels(targetPanels)}-panel preview locally; send the real decimated solve to CUDA.`,
      targetPanels,
      denseMatrixGb: denseGb,
      relativeWork: work,
    };
  }
  if (sourcePanels > 12_000 || work > 6 || denseGb > 1) {
    return {
      risk: "medium",
      label: "Mac preview only",
      detail: `Decimate to about ${formatPanels(targetPanels)} panels before local WebGPU/CPU testing.`,
      targetPanels,
      denseMatrixGb: denseGb,
      relativeWork: work,
    };
  }
  return {
    risk: "low",
    label: "Mac-safe",
    detail: "Small enough for local WebGPU/CPU iteration with the current Filament no-CUDA path.",
    targetPanels,
    denseMatrixGb: denseGb,
    relativeWork: work,
  };
}

function cudaRisk(sourcePanels: number | null, targetPanels: number | null, wakeIterations: number): PanelTargetEstimate {
  const denseGb = denseMatrixGb(targetPanels);
  const work = workScale(targetPanels, wakeIterations);
  if (!sourcePanels || !targetPanels || denseGb == null || work == null) {
    return {
      risk: "unknown",
      label: "Needs metadata",
      detail: "CUDA estimate improves once Thread can resolve the case, volume mesh, or surface mesh.",
      targetPanels: null,
      denseMatrixGb: null,
      relativeWork: null,
    };
  }
  if (sourcePanels > 180_000) {
    return {
      risk: "high",
      label: "CUDA after strong decimation",
      detail: `Target ${formatPanels(targetPanels)} panels first; full source panelization is still a large O(n²) solve.`,
      targetPanels,
      denseMatrixGb: denseGb,
      relativeWork: work,
    };
  }
  if (sourcePanels > 30_000 || work > 25 || denseGb > 6) {
    return {
      risk: "medium",
      label: "CUDA recommended",
      detail: `Use CUDA/dev GPU for the decimated ${formatPanels(targetPanels)}-panel solve; keep Mac for setup and previews.`,
      targetPanels,
      denseMatrixGb: denseGb,
      relativeWork: work,
    };
  }
  return {
    risk: "low",
    label: "CUDA comfortable",
    detail: "Should be fine on the dev GPU, and likely tolerable locally if the wake stays modest.",
    targetPanels,
    denseMatrixGb: denseGb,
    relativeWork: work,
  };
}

export function estimateFlow360PanelRunDifficulty(input: Flow360PanelRunEstimateInput): Flow360PanelRunEstimate {
  const wakeIterations = input.wakeIterations ?? 15;
  const config = input.fullConfig?.config ?? null;
  const explicitSurfacePanels = largestMatchingNumber(config, SURFACE_PANEL_KEYS);
  const explicitVolumeCells = largestMatchingNumber(config, VOLUME_CELL_KEYS);
  const bytes = storageBytes(
    input.surfaceMesh?.storageSize,
    input.volumeMesh?.storageSize,
    input.case?.storageSize,
    input.geometry?.storageSize,
  );
  const storagePanels = panelsFromStorage(
    input.surfaceMesh?.storageSize,
    input.volumeMesh?.storageSize,
    input.case?.storageSize,
    input.geometry?.storageSize,
  );
  const volumePanels = panelsFromVolumeCells(explicitVolumeCells);
  const sourcePanels = explicitSurfacePanels ?? storagePanels ?? volumePanels;
  const confidence = explicitSurfacePanels || explicitVolumeCells ? "metadata" : storagePanels ? "heuristic" : "unknown";
  const macPanels = recommendedMacPanels(sourcePanels);
  const cudaPanels = recommendedCudaPanels(sourcePanels);
  const facts: string[] = [];
  const warnings: string[] = [];

  if (input.case?.name) facts.push(`Case: ${input.case.name}`);
  else if (input.fullConfig?.case_name) facts.push(`Case: ${input.fullConfig.case_name}`);
  else if (input.caseId) facts.push(`Case ID: ${input.caseId}`);
  if (input.folder?.name) facts.push(`Flow360 project: ${input.folder.name}`);
  if (explicitSurfacePanels) facts.push(`Surface metadata reports about ${formatPanels(explicitSurfacePanels)} panels/faces.`);
  if (explicitVolumeCells) facts.push(`Volume metadata reports about ${formatPanels(explicitVolumeCells)} cells/elements.`);
  if (storagePanels && !explicitSurfacePanels) facts.push(`Storage-size heuristic estimates about ${formatPanels(storagePanels)} source panels.`);
  if (bytes) facts.push(`Largest entity payload: ${formatBytes(bytes)}.`);
  if (!sourcePanels) warnings.push("No mesh counts or storage sizes were exposed; this is an intake placeholder until Flow360 metadata resolves.");
  if (sourcePanels && sourcePanels > 60_000) warnings.push("Large aircraft-scale surface: decimate before local Mac/WebGPU work.");
  if (sourcePanels && denseMatrixGb(sourcePanels) != null && denseMatrixGb(sourcePanels)! > 12) {
    warnings.push("A dense direct panel matrix at source size would be too large; use matrix-free/decimated solves.");
  }

  return {
    confidence,
    sourcePanels,
    sourceDenseMatrixGb: denseMatrixGb(sourcePanels),
    estimatedVolumeCells: explicitVolumeCells,
    storageBytes: bytes,
    wakeIterations,
    mac: macRisk(sourcePanels, macPanels, wakeIterations),
    cuda: cudaRisk(sourcePanels, cudaPanels, wakeIterations),
    facts,
    warnings,
  };
}

export function formatPanels(value: number | null | undefined): string {
  if (!value) return "unknown";
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(value >= 10_000_000 ? 0 : 1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(value >= 100_000 ? 0 : 1)}k`;
  return String(Math.round(value));
}

export function formatBytes(value: number | null | undefined): string {
  if (!value) return "unknown";
  if (value >= 1_073_741_824) return `${(value / 1_073_741_824).toFixed(1)} GB`;
  if (value >= 1_048_576) return `${(value / 1_048_576).toFixed(1)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${value} B`;
}

export function formatGigabytes(value: number | null | undefined): string {
  if (value == null) return "unknown";
  if (value < 0.1) return `${(value * 1024).toFixed(0)} MB`;
  return `${value.toFixed(value >= 10 ? 0 : 1)} GB`;
}
